// All dates in Runna use Brazil timezone (America/Sao_Paulo)
// This ensures consistency regardless of server/browser timezone

export const TIMEZONE = 'America/Sao_Paulo'

/**
 * Parse a YYYY-MM-DD date string as a LOCAL date (not UTC).
 * Avoids the off-by-one bug where new Date("2026-04-14") parses as
 * UTC midnight, which becomes 2026-04-13 21:00 in São Paulo timezone.
 * Use this EVERYWHERE a date-only string (no time) is parsed.
 */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Get current date in Brazil timezone */
export function nowBrazil(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: TIMEZONE }))
}

/** Format a date-only string to Brazil Date object */
export function toBrazilDate(dateStr: string): Date {
  // For YYYY-MM-DD strings, use parseLocalDate to avoid UTC offset
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return parseLocalDate(dateStr)
  }
  // For full ISO timestamps, convert via timezone
  return new Date(new Date(dateStr).toLocaleString('en-US', { timeZone: TIMEZONE }))
}

/** Get today's date at midnight in Brazil timezone */
export function todayBrazil(): Date {
  const d = nowBrazil()
  d.setHours(0, 0, 0, 0)
  return d
}

/** True for Saturday (6) or Sunday (0). */
export function isWeekend(date: Date): boolean {
  const day = date.getDay()
  return day === 0 || day === 6
}

/**
 * Returns `date + n business days`, skipping weekends (no holiday calendar).
 * The starting `date` is NOT counted, so addBusinessDays(Wed, 2) returns Fri.
 */
export function addBusinessDays(date: Date, n: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  let added = 0
  while (added < n) {
    d.setDate(d.getDate() + 1)
    if (!isWeekend(d)) added++
  }
  return d
}

/**
 * Client approval deadline: send date + 2 business days.
 * Pass the actual submission date (e.g. `actual_script_date`). Returns a Date
 * at midnight representing the last day the client has to approve.
 */
export function clientApprovalDeadline(sendDateStr: string): Date {
  const base = /^\d{4}-\d{2}-\d{2}$/.test(sendDateStr)
    ? parseLocalDate(sendDateStr)
    : new Date(sendDateStr)
  base.setHours(0, 0, 0, 0)
  return addBusinessDays(base, 2)
}
